import {
  type DashboardContributor,
  MAX_CONTRIBUTORS_LISTED,
  type RevisionActorKind,
} from "@/services/dashboardRevisions";
import type { ModifiedAction } from "@/services/dashboardResponse";
import { SystemUser, getUserById, type User } from "@/services/user";

export interface RevisionAuthor {
  actor: number | null;
  actorName: string | null;
  actorKind: RevisionActorKind;
}

/** Fyr AI made the edit, for the person recorded as its actor: an Automagic
 * AI run, or an agent working through MCP. */
export function isAiAuthor(author: RevisionAuthor): boolean {
  return author.actorKind === "ai_agent" || author.actorKind === "mcp";
}

/** The author as the dashboard list's byline names them, so the same edit
 * reads the same in the history, the contributors and the list. Null for a
 * person whose name is not on record, and for a `system` entry: whoever it
 * records as its actor did not make it. A date roll the view made carries
 * the viewer who opened the dashboard. */
export function authorName(author: RevisionAuthor): string | null {
  if (author.actorKind === "system") return null;
  if (!isAiAuthor(author)) return author.actorName;
  return author.actorName
    ? `Fyr AI on behalf of ${author.actorName}`
    : "Fyr AI";
}

/** Fyr's mark for the AI and for an edit nobody made by hand, the person's
 * own picture otherwise. */
export function authorAvatarUser(
  author: RevisionAuthor,
  users: User[] | undefined,
) {
  if (isAiAuthor(author) || author.actorKind === "system") return SystemUser;
  // Deleted since. Not Fyr's mark, which would read as the AI's.
  if (author.actor === null) {
    return { id: 0, name: author.actorName ?? "Unknown" };
  }
  // The org's user list carries the profile picture; `actorName` is the
  // fallback for an author who has since left it or been deleted.
  return (
    getUserById(users, author.actor) ?? {
      id: author.actor,
      name: author.actorName ?? "Unknown",
    }
  );
}

/** What the byline's author did, for the line under it. Null for a touch
 * made before that was recorded. */
export function lastChangeText(dashboard: {
  modifiedAction?: ModifiedAction;
  modifiedSummary?: string;
  modifiedByAi: boolean;
}): string | null {
  if (dashboard.modifiedSummary) return dashboard.modifiedSummary;
  if (dashboard.modifiedAction !== "edit") return null;
  // An edit the funnel had no words for, titled as the history titles it.
  return dashboard.modifiedByAi ? "AI change" : "Edited in the dashboard";
}

export function contributorsCut(contributors: DashboardContributor[]): boolean {
  return contributors.length > MAX_CONTRIBUTORS_LISTED;
}

export function listedContributors(
  contributors: DashboardContributor[],
): DashboardContributor[] {
  return contributors.slice(0, MAX_CONTRIBUTORS_LISTED);
}

/** "3 contributors", or "8+ contributors" when the server had more. */
export function contributorCount(contributors: DashboardContributor[]): string {
  const count = listedContributors(contributors).length;
  const shown = contributorsCut(contributors) ? `${count}+` : `${count}`;
  return `${shown} contributor${count === 1 ? "" : "s"}`;
}

/** Who the dashboard list's byline names. */
export interface BylineAuthor {
  actor: number;
  ai: boolean;
}

/** The contributors with the byline's author first, so the face beside
 * "Last modified by" is theirs. Only when they have a revision: a move or an
 * Approve sets the byline without one, and someone who only undid and redid
 * has changed nothing the history panel could list them for. */
export function contributorsLedBy(
  contributors: DashboardContributor[],
  lead: BylineAuthor | null,
): DashboardContributor[] {
  if (!lead) return contributors;
  const first = contributors.find((contributor) =>
    isBylineAuthor(contributor, lead),
  );
  if (!first) return contributors;
  // Only the entry it found: one person can have both an `mcp` and an
  // `ai_agent` entry, and both match an AI byline.
  return [
    first,
    ...contributors.filter((contributor) => contributor !== first),
  ];
}

export function leadsContributors(
  contributors: DashboardContributor[],
  lead: BylineAuthor,
): boolean {
  return contributors.length > 0 && isBylineAuthor(contributors[0], lead);
}

function isBylineAuthor(
  contributor: DashboardContributor,
  lead: BylineAuthor,
): boolean {
  return (
    contributor.actor === lead.actor && isAiAuthor(contributor) === lead.ai
  );
}
